import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardHeader, CardContent, Divider, CircularProgress, Alert, Paper, useTheme, Tooltip, IconButton, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, BarChart, Bar, Cell } from 'recharts';
import { format, parseISO, isValid, parse as parseDate } from 'date-fns';
import VisibilityIcon from '@mui/icons-material/Visibility';
import apiClient from '../utils/api';
import ForumIcon from '@mui/icons-material/Forum';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';

const StatDisplay = ({ title, value, loading, icon }) => (
    <Card sx={{ height: '100%' }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 2 }}>
             {icon && React.cloneElement(icon, { sx: { fontSize: 40, color: 'primary.main', mr: 2 } })}
             <Box sx={{ textAlign: 'left' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>{title}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {loading ? <CircularProgress size={24}/> : value ?? 'N/A'}
                </Typography>
             </Box>
        </CardContent>
    </Card>
);

const ChatAnalyticsPage = () => {
    const [dailyCounts, setDailyCounts] = useState([]);
    const [sessionsByUser, setSessionsByUser] = useState([]);
    const [messagesByUser, setMessagesByUser] = useState([]);
    const [chatSessions, setChatSessions] = useState([]);
    const [loading, setLoading] = useState({ daily: true, sessions: true, messages: true, list: true });
    const [error, setError] = useState('');
    const theme = useTheme();
    const navigate = useNavigate();

    // State for Top Users chart
    const [topUsersCount, setTopUsersCount] = useState(10); // Default top 10

    // Regular expression to extract the timestamp (YYYYMMDDHHMMSS) from the session ID
    // Example ID: Dr_Bill-mattrobinson@qstarlabs.ai-20250401172858-c34b1d12-0cd3-4beb-a4f1-f8b73e3c9c35
    const sessionIdTimestampRegex = /(?:.*-)(\d{14})(?:-.*)/;

    useEffect(() => {
        const fetchData = async () => {
             setLoading({ daily: true, sessions: true, messages: true, list: true });
             setError('');
            try {
                const [dailyRes, sessionsRes, messagesRes, listRes] = await Promise.allSettled([
                    apiClient.get('/analytics/chats/daily'),
                    apiClient.get('/analytics/chats/sessions_by_user'),
                    apiClient.get('/analytics/chats/messages_by_user'),
                    apiClient.get('/chats/')
                ]);

                let errors = [];

                if (dailyRes.status === 'fulfilled' && typeof dailyRes.value?.data === 'object') {
                    const data = dailyRes.value.data;
                    console.log("Raw Daily Counts Data:", data);
                    const formattedCounts = Object.entries(data).map(([date, count]) => {
                         const parsedDate = parseISO(date);
                         return { date: isValid(parsedDate) ? format(parsedDate, 'MMM d') : date, isoDate: date, count };
                    }).sort((a, b) => a.isoDate.localeCompare(b.isoDate));
                    setDailyCounts(formattedCounts);
                } else {
                    const reason = dailyRes.reason || "Unknown error fetching daily counts";
                    console.error("Failed daily counts:", reason);
                    errors.push(reason?.response?.data?.detail || reason?.message || 'loading daily counts');
                }
                setLoading(prev => ({ ...prev, daily: false }));

                if (sessionsRes.status === 'fulfilled' && typeof sessionsRes.value?.data === 'object') {
                    const data = sessionsRes.value.data;
                    console.log("Raw Sessions Per User Data:", data);
                    const formattedSessions = Object.entries(data)
                        .filter(([email]) => email !== "error_parsing_id" && email !== "unknown_email")
                        .map(([email, count]) => ({ id: email, email, count }));
                     setSessionsByUser(formattedSessions);
                 } else {
                    const reason = sessionsRes.reason || "Unknown error fetching sessions per user";
                    console.error("Failed sessions by user:", reason);
                    errors.push(reason?.response?.data?.detail || reason?.message || 'loading sessions per user');
                 }
                 setLoading(prev => ({ ...prev, sessions: false }));

                 if (messagesRes.status === 'fulfilled' && typeof messagesRes.value?.data === 'object') {
                    const data = messagesRes.value.data;
                    console.log("Raw Messages Per User Data:", data);
                    const sortedMessages = Object.entries(data)
                        .filter(([email]) => email !== "error_parsing_id" && email !== "unknown_email")
                        .map(([email, count]) => ({ id: email, email, count }))
                        .sort((a, b) => b.count - a.count);
                    setMessagesByUser(sortedMessages);
                 } else {
                    const reason = messagesRes.reason || "Unknown error fetching messages per user";
                    console.error("Failed messages by user:", reason);
                     errors.push(reason?.response?.data?.detail || reason?.message || 'loading messages per user');
                 }
                 setLoading(prev => ({ ...prev, messages: false }));

                 if (listRes.status === 'fulfilled' && Array.isArray(listRes.value?.data)) {
                    const data = listRes.value.data;
                    console.log("Raw Chat List Data:", data);
                    setChatSessions(data.map(session => ({
                        ...session,
                        id: session.id,
                        created_at: session.created_at || null // Ensure field exists
                    })));
                 } else {
                    const reason = listRes.reason || "Unknown error fetching chat list";
                    console.error("Failed chat list:", reason);
                     errors.push(reason?.response?.data?.detail || reason?.message || 'loading chat list');
                 }
                 setLoading(prev => ({ ...prev, list: false }));

                 if (errors.length > 0) {
                    setError(`Failed loading some data: ${errors.join(', ')}.`);
                 }

            } catch (err) {
                console.error("General Error fetching chat analytics data:", err);
                const errorMessage = err.response?.data?.detail || err.message || 'An unexpected network error occurred.';
                setError(errorMessage);
                setLoading({ daily: false, sessions: false, messages: false, list: false });
            }
        };

        fetchData();
    }, []);

    // Calculate totals - ensure state variables are arrays before reducing
    const totalSessions = Array.isArray(sessionsByUser)
        ? sessionsByUser.reduce((sum, item) => sum + (Number(item.count) || 0), 0) // Ensure count is treated as number
        : 0; // Default to 0 if not an array

    const totalMessages = Array.isArray(messagesByUser)
        ? messagesByUser.reduce((sum, item) => sum + (Number(item.count) || 0), 0) // Ensure count is treated as number
        : 0; // Default to 0 if not an array

    console.log("Calculated Totals:", { totalSessions, totalMessages }); // LOGGING
    console.log("Current State:", { sessionsByUser, messagesByUser }); // LOGGING

    // Prepare data for top users chart
    const topUsersData = messagesByUser.slice(0, topUsersCount);
    const barColors = [theme.palette.primary.main, theme.palette.secondary.main, theme.palette.info.main, theme.palette.warning.main, theme.palette.success.main, theme.palette.error.main, theme.palette.grey[500]];

    // --- Column Definitions ---
    const sessionsColumns = [
        { field: 'email', headerName: 'User Email', flex: 1 },
        { field: 'count', headerName: 'Sessions', width: 150, type: 'number', align: 'right', headerAlign: 'right' },
    ];
    const messagesColumns = [
        { field: 'email', headerName: 'User Email', flex: 1 },
        { field: 'count', headerName: 'Messages', width: 150, type: 'number', align: 'right', headerAlign: 'right' },
    ];
     const chatListColumns = [
         { field: 'id', headerName: 'Session ID', flex: 1.5, minWidth: 300 },
         { field: 'email', headerName: 'User Email', flex: 1, minWidth: 200 },
         {
             field: 'created_at', headerName: 'Started At', width: 180, type: 'dateTime',
             valueGetter: (params) => {
                  const value = params.value;
                  const row = params.row;

                  console.log(`valueGetter called for row.id: ${row?.id}, initial value (created_at):`, value); // DEBUG LOG
                  let date = null;

                  // 1. Try parsing the 'created_at' field value
                  if (value) {
                      // Check if it's a Firestore Timestamp-like object
                      if (typeof value === 'object' && value !== null && typeof value.seconds === 'number') {
                           try {
                               // Convert Firestore seconds/nanos to JS Date
                               date = new Date(value.seconds * 1000 + (value.nanoseconds || 0) / 1000000);
                               if (isValid(date)) {
                                    console.log(`  -> Parsed date from Firestore Timestamp object:`, date); // DEBUG LOG
                               } else {
                                    console.warn(`  -> Constructed invalid date from Timestamp object:`, value); // DEBUG LOG
                                    date = null; // Reset if invalid
                               }
                           } catch(e) {
                                console.error(`  -> Error converting Firestore Timestamp object:`, value, e); // DEBUG LOG
                                date = null;
                           }
                      // Check if it's a string (might be ISO format)
                      } else if (typeof value === 'string') {
                           try {
                               const parsedIso = parseISO(value);
                               if (isValid(parsedIso)) {
                                    console.log(`  -> Parsed date from ISO string:`, parsedIso); // DEBUG LOG
                                    date = parsedIso;
                               } else {
                                    console.warn(`  -> Invalid date from string field: ${value}`); // DEBUG LOG
                               }
                           } catch(e) {
                                console.warn(`  -> Error parsing string field: ${value}`, e); // DEBUG LOG
                           }
                      } else {
                           console.warn(`  -> 'created_at' field has unexpected type: ${typeof value}`, value); // DEBUG LOG
                      }
                  } else {
                       console.log("  -> 'created_at' field is empty or null."); // DEBUG LOG
                  }

                  // 2. If no valid date found yet, try parsing from ID (using params.row.id)
                  if (!date && row?.id) {
                      console.log(`  -> Attempting to parse from ID: ${row.id}`); // DEBUG LOG
                      try {
                          const match = row.id.match(sessionIdTimestampRegex);
                          if (match && match[1]) {
                              const timestampString = match[1];
                              console.log(`    -> Regex matched timestampString: ${timestampString}`); // DEBUG LOG
                              const parsedFromId = parseDate(timestampString, 'yyyyMMddHHmmss', new Date());
                              if (isValid(parsedFromId)) {
                                  console.log(`    -> Successfully parsed date from ID:`, parsedFromId); // DEBUG LOG
                                  date = parsedFromId;
                              } else {
                                  console.warn(`    -> Failed to parse timestamp string '${timestampString}' from ID (isValid=false).`); // DEBUG LOG
                              }
                          } else {
                               console.warn(`    -> Regex did not match expected format for ID: ${row.id}`); // DEBUG LOG
                          }
                      } catch (parseError) {
                          console.error(`    -> Error during regex/parsing timestamp from ID ${row.id}:`, parseError); // DEBUG LOG
                      }
                  } else if (!row?.id) {
                       console.warn("  -> Cannot parse from ID because row.id is missing."); // DEBUG LOG
                  } else if (date) {
                      console.log("  -> Skipping ID parse because date was found in 'created_at'."); // DEBUG LOG
                  }

                  console.log(`  -> Final date returned by valueGetter:`, date); // DEBUG LOG
                  return date; // Return the valid Date object or null
             },
             // Ensure sorting works correctly with Date objects or null
             sortComparator: (v1, v2) => {
                 const d1 = v1 ? v1.getTime() : 0; // Treat null/invalid as earliest
                 const d2 = v2 ? v2.getTime() : 0;
                 return d1 - d2;
             },
             renderCell: (params) => params.value ? format(params.value, 'Pp') : 'N/A'
         },
         { field: 'message_count', headerName: 'Msg Count', width: 120, type: 'number', align: 'right', headerAlign: 'right' },
         {
             field: 'actions', headerName: 'Actions', width: 100, sortable: false, filterable: false, align: 'center', headerAlign: 'center',
             renderCell: (params) => (
                 <Tooltip title="View Chat History" arrow>
                     <IconButton size="small" onClick={() => navigate(`/chats/${params.row.id}`)}>
                         <VisibilityIcon fontSize="small" />
                     </IconButton>
                 </Tooltip>
             ),
         },
     ];
    // --- End Column Definitions ---

    return (
        <Box sx={{ p: 0 }}>
             <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>Chat Analytics</Typography>

             {/* Display collected error messages */}
             {error && <Alert severity="warning" sx={{ mb: 3 }}>{`Errors loading data: ${error}`}</Alert>}
             {/* General loading indicator can be removed if StatDisplay handles it */}
             {/* {isLoading && !error && <CircularProgress sx={{ display: 'block', margin: 'auto', mb: 3 }}/>} */}

            {/* --- UPDATED StatDisplay Usage --- */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={6}>
                     <StatDisplay
                        title="Total Chat Sessions"
                        value={loading.sessions ? null : totalSessions}
                        loading={loading.sessions}
                        icon={<ForumIcon />}
                      />
                 </Grid>
                 <Grid item xs={12} sm={6} md={6}>
                     <StatDisplay
                        title="Total Messages Sent"
                        value={loading.messages ? null : totalMessages}
                        loading={loading.messages}
                        icon={<QuestionAnswerIcon />}
                      />
                 </Grid>
            </Grid>
            {/* --- END UPDATE --- */}


            <Grid container spacing={3} sx={{ mb: 4 }}>
                 {/* Daily Chat Count Chart */}
                 <Grid item xs={12} md={6}>
                    <Card sx={{ height: '40dvh', display: 'flex', flexDirection: 'column' }}>
                        <CardHeader title="Daily Chat Sessions Started" />
                        <Divider />
                        <CardContent sx={{ flexGrow: 1, overflow: 'auto', p: 1, '& .recharts-responsive-container': { minHeight: '200px' } }}>
                             {loading.daily ? <Box sx={{textAlign: 'center', p:5}}><CircularProgress /></Box> : dailyCounts.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={dailyCounts} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
                                        <XAxis dataKey="date" axisLine={false} tickLine={false} />
                                        <YAxis allowDecimals={false} axisLine={false} tickLine={false} />
                                        <RechartsTooltip />
                                        <Line type="monotone" dataKey="count" name="Sessions" stroke={theme.palette.primary.main} strokeWidth={2} activeDot={{ r: 8 }} dot={false} />
                                    </LineChart>
                                </ResponsiveContainer>
                             ) : <Typography sx={{textAlign: 'center', p: 3}} color="text.secondary">No daily data available.</Typography>}
                        </CardContent>
                    </Card>
                </Grid>

                 {/* Top Users by Message Count Chart */}
                 <Grid item xs={12} md={6}>
                    <Card sx={{ height: '40dvh', display: 'flex', flexDirection: 'column' }}>
                        <CardHeader
                            title="Top Users by Message Count"
                            action={
                                <FormControl size="small" sx={{ minWidth: 120 }} disabled={loading.messages}>
                                    <InputLabel id="top-users-select-label">Show Top</InputLabel>
                                    <Select
                                        labelId="top-users-select-label"
                                        id="top-users-select"
                                        value={topUsersCount}
                                        label="Show Top"
                                        onChange={(e) => setTopUsersCount(Number(e.target.value))}
                                    >
                                        <MenuItem value={5}>5</MenuItem>
                                        <MenuItem value={10}>10</MenuItem>
                                        <MenuItem value={20}>20</MenuItem>
                                    </Select>
                                </FormControl>
                            }
                        />
                        <Divider />
                        <CardContent sx={{ flexGrow: 1, overflow: 'auto', p: 1, '& .recharts-responsive-container': { minHeight: '300px' } }}>
                            {loading.messages ? <Box sx={{textAlign: 'center', p:5}}><CircularProgress /></Box> : topUsersData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={topUsersData} layout="vertical" margin={{ top: 5, right: 30, left: 120, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={theme.palette.divider}/>
                                        <XAxis type="number" axisLine={false} tickLine={false}/>
                                        <YAxis type="category" dataKey="email" width={120} interval={0} fontSize={10} tick={{width: 115}} axisLine={false} tickLine={false}/>
                                        <RechartsTooltip />
                                        <Bar dataKey="count" name="Messages" barSize={20}>
                                             {topUsersData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={barColors[index % barColors.length]} />
                                             ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                             ) : <Typography sx={{textAlign: 'center', p: 3}} color="text.secondary">No user message data available.</Typography>}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Sessions/Messages Per User Tables */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                 <Grid item xs={12} md={6}>
                     <Paper sx={{ height: 400, width: '100%', display: 'flex', flexDirection: 'column', /* borderRadius: theme.shape.borderRadius, */ border: `1px solid ${theme.palette.divider}`, boxShadow: theme.shadows[1] }}>
                         <Typography variant="h6" sx={{ p: 2, fontWeight: 600, fontSize: '1.1rem', borderBottom: `1px solid ${theme.palette.divider}` }}>Sessions Per User</Typography>
                         <Box sx={{ flexGrow: 1 }}>
                             <DataGrid
                                 getRowId={(row) => row.id || row.email}
                                 rows={sessionsByUser}
                                 columns={sessionsColumns}
                                 loading={loading.sessions}
                                 pageSizeOptions={[5, 10, 25]}
                                 density="compact"
                                 sx={{ border: 0, '& .MuiDataGrid-main': { flexGrow: 1 } }}
                                 autoHeight={false}
                                 hideFooter={sessionsByUser.length <= 5}
                             />
                         </Box>
                     </Paper>
                 </Grid>
                <Grid item xs={12} md={6}>
                     <Paper sx={{ height: 400, width: '100%', display: 'flex', flexDirection: 'column', /* borderRadius: theme.shape.borderRadius, */ border: `1px solid ${theme.palette.divider}`, boxShadow: theme.shadows[1] }}>
                         <Typography variant="h6" sx={{ p: 2, fontWeight: 600, fontSize: '1.1rem', borderBottom: `1px solid ${theme.palette.divider}` }}>Messages Per User</Typography>
                          <Box sx={{ flexGrow: 1 }}>
                             <DataGrid
                                 getRowId={(row) => row.id || row.email}
                                 rows={messagesByUser}
                                 columns={messagesColumns}
                                 loading={loading.messages}
                                 pageSizeOptions={[5, 10, 25]}
                                 density="compact"
                                 sx={{ border: 0, '& .MuiDataGrid-main': { flexGrow: 1 } }}
                                 autoHeight={false}
                                 hideFooter={messagesByUser.length <= 5}
                             />
                          </Box>
                     </Paper>
                 </Grid>
            </Grid>

            {/* Chat Session List Table */}
            <Grid item xs={12}>
                 <Paper sx={{ height: 600, width: '100%', display: 'flex', flexDirection: 'column', /* borderRadius: theme.shape.borderRadius, */ border: `1px solid ${theme.palette.divider}`, boxShadow: theme.shadows[1] }}>
                     <Typography variant="h6" sx={{ p: 2, fontWeight: 600, fontSize: '1.1rem', borderBottom: `1px solid ${theme.palette.divider}` }}>All Chat Sessions</Typography>
                     <Box sx={{ flexGrow: 1 }}>
                         <DataGrid
                             getRowId={(row) => row.id}
                             rows={chatSessions}
                             columns={chatListColumns}
                             loading={loading.list}
                             pageSizeOptions={[10, 25, 50]}
                             density="compact"
                             sx={{ border: 0, '& .MuiDataGrid-main': { flexGrow: 1 } }}
                             autoHeight={false}
                          />
                      </Box>
                 </Paper>
             </Grid>
        </Box>
    );
};

export default ChatAnalyticsPage;